import { Component, OnInit, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MODAL_DATA } from '@stockaid/services';
import { BaseComponent } from '@stockaid/core';
import { catchError, finalize, first, tap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { downloadFile } from 'src/app/core/utils';
import { SyncService } from 'src/app/core/services/sync.service';
import {
  ExcelUploadModalData,
  ExcelUploadIssue,
  ExcelRowType,
  ExcelUploadIdentityRow,
} from './excel-upload-error-modal.interface';

@Component({
  selector: 'app-excel-upload-error-modal',
  templateUrl: './excel-upload-error-modal.component.html',
  styleUrls: ['./excel-upload-error-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExcelUploadErrorModalComponent extends BaseComponent implements OnInit {
  private readonly MAX_DISPLAY_ERRORS = 500;

  issues: ExcelUploadIssue[] = [];
  reportLink: string | null = null;
  reportId: string | null = null;
  totalIssueCount = 0;
  totalCount = 0;
  successCount = 0;
  isLoading = false;
  hintMessage: string | null = null;
  gridHeight = 400;

  constructor(
    @Inject(MODAL_DATA) public data: ExcelUploadModalData,
    public activeModal: NgbActiveModal,
    private syncService: SyncService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.issues = this.preprocessIssues(this.data?.errors || []);
    this.reportLink = this.data?.reportLink || null;
    this.totalIssueCount = this.data?.errorNumber ?? this.issues.length;
    this.totalCount = this.data?.totalCount ?? 0;
    this.successCount = this.data?.successCount ?? 0;

    // Calculate grid height (Max 70vh)
    const vh70 = window.innerHeight * 0.7;
    const estimatedHeight = Math.max(150, Math.min(vh70, (this.issues.length || 10) * 75 + 40)); 
    this.gridHeight = estimatedHeight;

    if (this.reportLink) {
      this.reportId = this.reportLink.split('/').pop() ?? 'report';
    }

    if (this.totalIssueCount > this.issues.length) {
      this.hintMessage = `Showing ${this.issues.length} of ${this.totalIssueCount} issues. Download the full report to see all.`;
    }

    if (this.reportLink && !this.issues.length) {
      this.loadAdditionalErrors();
    }
  }

  private loadAdditionalErrors(): void {
    if (!this.reportLink || !this.data.reportColumns) return;

    this.isLoading = true;
    this.syncService.getErrorReport(this.reportLink, this.reportId ?? '', false).pipe(
      this.autoCleanUp(),
      first(),
      tap((lines: any) => {
        // syncService.getErrorReport might already return parsed rows or raw strings depending on implementation
        // PriceUploadErrorModal uses lines.slice(1...) suggesting raw lines
        
        if (Array.isArray(lines)) {
          if (typeof lines[0] === 'string') {
            // Raw CSV lines
            // Raw CSV lines
            const parsedIssues = lines.slice(1, this.MAX_DISPLAY_ERRORS + 1).map((line) => {
              const parts = line.split(',');
              const issue: any = {};
              this.data.reportColumns?.forEach((col, index) => {
                issue[col] = parts[index];
              });
              return issue as ExcelUploadIssue;
            });
            this.issues = this.preprocessIssues(parsedIssues);
          } else {
            // Already parsed objects
            this.issues = this.preprocessIssues(lines.slice(0, this.MAX_DISPLAY_ERRORS));
          }
        }

        if (this.totalIssueCount > this.MAX_DISPLAY_ERRORS) {
          this.hintMessage = `Showing ${this.MAX_DISPLAY_ERRORS} of ${this.totalIssueCount} issues. Download the full report to see all.`;
        } else {
          this.hintMessage = '';
        }

        // Recalculate height after data is loaded
        const vh70 = window.innerHeight * 0.7;
        this.gridHeight = Math.max(150, Math.min(vh70, (this.issues.length || 10) * 75 + 40));
        this.cd.markForCheck();
      }),
      catchError(() => {
        this.hintMessage = 'Failed to load issues. Download the report to view them.';
        this.cd.markForCheck();
        return EMPTY;
      }),
      finalize(() => {
        this.isLoading = false;
        this.cd.markForCheck();
      }),
    ).subscribe();
  }

  rowCallback = (context: any): any => {
    if (context.dataItem.type === 'error') {
      return { 'row-danger': true };
    } else if (context.dataItem.type === 'warning') {
      return { 'row-warning': true };
    }
    return {};
  };

  downloadErrors(): void {
    if (!this.reportLink) return;
    downloadFile(this.reportLink, `upload-errors-${this.reportId}.csv`);
  }

  onClose(): void {
    this.activeModal.close();
  }

  /**
   * Pre-processes issues to resolve identity information once.
   */
  private preprocessIssues(issues: ExcelUploadIssue[]): ExcelUploadIssue[] {
    return issues.map(item => ({
      ...item,
      identities: this.resolveIdentity(item),
    }));
  }

  /**
   * Resolves the identity information to display in the grid.
   * Handles SKU/ASIN (Parent), Campaign/AdId (Child), and Ambiguous cases.
   */
  private resolveIdentity(
    item: ExcelUploadIssue,
  ): ExcelUploadIdentityRow[] {
    const identities: ExcelUploadIdentityRow[] = [];

    const rowType = item.rowType;
    const hasAd = !!item.adId;
    const hasParent = !!item.sku;

    // Handle Ambiguous rows first
    if (rowType === ExcelRowType.AMBIGUOUS) {
      identities.push({
        mainText: item.sku || 'N/A',
        tagText: item.asin || '',
        label: 'ASIN',
        icon: 'fa-exclamation-triangle',
      });
      identities.push({
        mainText: item.campaignName || '—',
        tagText: item.adId || '',
        label: 'Ad Id',
        icon: 'fa-exclamation-triangle',
        isMetadata: true,
      });
      return identities;
    }

    // Row 1: Primary Product Info (SKU/ASIN)
    if (hasParent) {
      const showParent = rowType === ExcelRowType.PARENT || rowType === ExcelRowType.CHILD || !rowType;
      if (showParent) {
        identities.push({
          mainText: item.sku || 'N/A',
          tagText: item.asin || '',
          label: 'ASIN',
          icon: 'fa-box',
        });
      }
    }

    // Row 2: Ad-specific Info (Campaign Name / Ad Id)
    if (hasAd) {
      const showAd = rowType === ExcelRowType.CHILD || !rowType;
      if (showAd) {
        identities.push({
          mainText: item.campaignName || '—',
          tagText: item.adId || '',
          label: 'Ad Id',
          icon: 'fa-bullhorn',
          isMetadata: true,
        });
      }
    }

    if (identities.length === 0) {
      identities.push({
        mainText: 'Unidentified Row',
        tagText: '',
        label: '',
        icon: 'fa-times-circle',
      });
    }

    return identities;
  }
}
